import { vec2 } from 'gl-matrix';

import {
  DEFAULT_DATABASE_EMBED_SIZE,
  DEFAULT_LARGE_EMBED_SIZE,
  DEFAULT_NATIVE_DATABASE_EMBED_MIN_SIZE,
} from '@canvas/constants/elements/smart-link';
import { issueElementType } from '@canvas/model/elements/issue';
import {
  type SmartLinkElementData,
  type SmartLinkKind,
  type SmartLinkSizeInfo,
  type SmartLinkViewInfo,
  smartLinkElementType,
} from '@canvas/model/elements/smart-link';
import { Vector2 } from '@canvas/vector';

export const defaultSmartLinkSizeMap: SmartLinkSizeInfo = {
  inline: { width: 345, height: 24 },
  embed: { width: 500, height: 347 },
  data: { width: 345, height: 105 },
  preview: { width: 280, height: 277 },
};

export const defaultLargeSmartLinkSizeMap: SmartLinkSizeInfo = {
  ...defaultSmartLinkSizeMap,
  embed: DEFAULT_LARGE_EMBED_SIZE,
};

export const defaultLargeDatabaseEmbedSizeMap: SmartLinkSizeInfo = {
  ...defaultSmartLinkSizeMap,
  embed: DEFAULT_DATABASE_EMBED_SIZE,
};

export const MINIMUM_SMART_LINK_NON_INLINE_SIZE = vec2.fromValues(280, 105);
export const MINIMUM_INLINE_SIZE = vec2.fromValues(
  defaultSmartLinkSizeMap.inline.width,
  defaultSmartLinkSizeMap.inline.height
);
export const MINIMUM_ISSUE_LINK_SIZE = vec2.fromValues(345, 105);
export const MINIMUM_NATIVE_DATABASE_EMBED_SIZE = vec2.fromValues(
  DEFAULT_NATIVE_DATABASE_EMBED_MIN_SIZE.width,
  DEFAULT_NATIVE_DATABASE_EMBED_MIN_SIZE.height
);

export const calculateSmartLinkOrIssueMinimumSize = (
  type: 'issue' | 'smartLink',
  kind: SmartLinkKind,
  url?: string
): vec2 => {
  if (kind === 'inline') {
    return MINIMUM_INLINE_SIZE;
  }

  if (type === issueElementType) {
    return MINIMUM_ISSUE_LINK_SIZE;
  }

  if (kind === 'embed' && url) {
    try {
      const urlObj = new URL(url);
      if (isDatabaseUrl(urlObj)) {
        return MINIMUM_NATIVE_DATABASE_EMBED_SIZE;
      }
    } catch (error) {
      // Defensive programming... should never happen.
      return MINIMUM_SMART_LINK_NON_INLINE_SIZE;
    }
  }

  return MINIMUM_SMART_LINK_NON_INLINE_SIZE;
};

export const smartLinkElementDataFromURL = (
  url: URL
): Omit<SmartLinkElementData, 'id'> => {
  const defaultKind: SmartLinkKind = 'block';
  const supportedViews: SmartLinkKind[] = [defaultKind];

  const { kind, size: defaultSize } = getSmartLinkDefaultViewInfo(url);

  if (kind !== defaultKind) {
    supportedViews.push(kind);
  }

  let size = new Vector2(defaultSize.data.width, defaultSize.data.height);
  if (kind === 'embed') {
    size = new Vector2(defaultSize.embed.width, defaultSize.embed.height);
  }

  return {
    type: smartLinkElementType,
    url: url.toString(),
    supportedViews,
    kind,
    size,
    position: new Vector2(0, 0),
    supportsPreview: false,
  };
};

const embedViewInfo: SmartLinkViewInfo = {
  kind: 'embed',
  size: defaultSmartLinkSizeMap,
};

const largeEmbedViewInfo: SmartLinkViewInfo = {
  kind: 'embed',
  size: defaultLargeSmartLinkSizeMap,
};

/**
 * This map defines the default kind value for certain known hosts.
 */
const smartLinkEmbedViewMap: { [hostName: string]: SmartLinkViewInfo } = {
  'docs.google.com': largeEmbedViewInfo,
  'drive.google.com': largeEmbedViewInfo,
  'sheets.google.com': largeEmbedViewInfo,
  'figma.com': largeEmbedViewInfo,
  'app.mural.co': largeEmbedViewInfo,
  'invisionapp.com': largeEmbedViewInfo,
  'invis.io': largeEmbedViewInfo,
  'miro.com': largeEmbedViewInfo,
  'app.zeplin.io': largeEmbedViewInfo,
  'zpl.io': largeEmbedViewInfo,
  'gist.github.com': largeEmbedViewInfo,
  'youtube.com': embedViewInfo,
  'twitter.com': embedViewInfo,
  'open.spotify.com': embedViewInfo,
  'vimeo.com': embedViewInfo,
  'dailymotion.com': embedViewInfo,
  'media.giphy.com': embedViewInfo,
  'loom.com': embedViewInfo,
  'stage.loom.com': embedViewInfo,
};

export const isLoomUrl = (url: string) => {
  const host = new URL(url).hostname;
  return validLoomURLs.has(host);
};

const isDatabaseUrl = (url: URL) => {
  const isLocalDatabaseUrl =
    url.hostname === 'databases.localhost' ||
    (url.hostname === 'canvas.localhost' && url.pathname.includes('/databases-dev/')) ||
    (url.hostname === 'localhost' && url.pathname.includes('/databases-dev/'));
  return confluenceDatabaseRegex.test(url.pathname) || isLocalDatabaseUrl;
};

const validLoomURLs = new Set(['loom.com', 'www.loom.com', 'stage.loom.com']);
const atlassianURlRegex =
  /(atlassian.net|atlassian.com|atlassian.cloud|jira-dev.com|jira.com|bitbucket.org)/;
const confluenceDatabaseRegex = /\/wiki\/spaces\/?[^/]*\/database\/\d+(\?\/)?/;

export const isAtlassianUrl = (url: URL): boolean => {
  return atlassianURlRegex.test(url.hostname);
};

export const getSmartLinkDefaultViewInfo = (url: URL) => {
  if (isDatabaseUrl(url)) {
    return {
      kind: 'embed' as SmartLinkKind,
      size: defaultLargeDatabaseEmbedSizeMap,
    };
  }

  // atlassian embeds get a larger default size and render as block
  const hostName = url.hostname.replace('www.', '');
  if (isAtlassianUrl(url)) {
    return {
      kind: 'block' as SmartLinkKind,
      size: defaultLargeSmartLinkSizeMap,
    };
  }

  return (
    smartLinkEmbedViewMap[hostName] || {
      kind: 'block' as SmartLinkKind,
      size: defaultSmartLinkSizeMap,
    }
  );
};

export const getLinkAppearance = (kind: SmartLinkKind, canPreview: boolean) => {
  return kind === 'block' ? (canPreview ? 'preview' : 'data') : kind;
};

export type CalculateSmartLinkOrIssueSizeInput = {
  type: typeof smartLinkElementType | typeof issueElementType;
  url: string;
  kind: SmartLinkKind;
  supportsPreview: boolean;
};

export const calculateSmartLinkOrIssueSize = ({
  type,
  url,
  kind,
  supportsPreview,
}: CalculateSmartLinkOrIssueSizeInput) => {
  /**
   * TODO: {@link https://product-fabric.atlassian.net/browse/CNS-17611}
   * Issues _never_ support preview.
   *
   * Historically we've set supportsPreview to true for issues and additionally
   * stored these values in the Yjs document.
   *
   * We've attempted to migrate these values in the past but as a failsafe
   * option, we're explicitly hard coding the value to false here until
   * persistent values are removed from the Yjs document.
   */
  const correctedSupportsPreview = type === issueElementType ? false : supportsPreview;
  const linkType = getLinkAppearance(kind, correctedSupportsPreview);

  try {
    const { size: defaultSize } = getSmartLinkDefaultViewInfo(new URL(url));

    return defaultSize[linkType];
  } catch (e) {
    // Fallback to default size if URL parsing fails
    return defaultSmartLinkSizeMap[linkType];
  }
};

export {
  sendSmartLinkConversionEvent,
  type SmartLinkConversionAttributes,
  type SendSmartLinkConversionEventParams,
} from './smart-link-conversion-analytics';
